import { useState, FormEvent } from "react";
import {
  firebaseSignIn,
  firebaseSignUp,
  firebasePasswordReset,
} from "../firebase/AuthService";
import { useNavigate } from "react-router-dom";
import { FirebaseError } from "firebase/app";
import logo from "../assets/logo.png";
import { getVersionApp } from "../data/memory";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [birthDate, setBirthDate] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<
    string | null
  >(null);
  const [displayNameError, setDisplayNameError] = useState<string | null>(null);
  const [birthDateError, setBirthDateError] = useState<string | null>(null);
  const [resetEmail, setResetEmail] = useState("");
  const [resetEmailError, setResetEmailError] = useState<string | null>(null);

  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setEmailError(null);
    setPasswordError(null);

    if (!email || !password) {
      if (!email) setEmailError("Email obrigatório");
      if (!password) setPasswordError("Senha obrigatória");
      return;
    }
    setLoading(true);
    try {
      await firebaseSignIn({ email, password });
      navigate("/lista");
    } catch (err) {
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case "auth/invalid-email":
            setError("Email inválido");
            break;
          case "auth/wrong-password":
            setError("Senha incorreta");
            break;
          case "auth/user-not-found":
            setError("Usuário não encontrado");
            break;
          case "auth/invalid-credential":
            setError("Credenciais inválidas");
            break;
          default:
            setError("Falha no login. Tente novamente.");
        }
      } else {
        setError("Erro inesperado. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const [loading, setLoading] = useState(false);

  const handleRegister = async (e: FormEvent) => {
    if (loading) return; // impede múltiplas execuções
    setLoading(true);
    e.preventDefault();
    setError(null);
    setEmailError(null);
    setPasswordError(null);
    setConfirmPasswordError(null);
    setDisplayNameError(null);
    setBirthDateError(null);

    if (new Date(birthDate) > new Date()) {
      setBirthDateError("A data de aniversário não pode ser no futuro");
      setLoading(false);
      return;
    }

    if (!email || !password || !confirmPassword || !displayName || !birthDate) {
      if (!email) setEmailError("Email obrigatório");
      if (!password) setPasswordError("Senha obrigatória");
      if (!confirmPassword) setConfirmPasswordError("Confirme a senha");
      if (!displayName) setDisplayNameError("Nome obrigatório");
      if (!birthDate) setBirthDateError("Data de aniversário obrigatória");
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError("As senhas não coincidem");
      setLoading(false);
      return;
    }

    try {
      await firebaseSignUp({ email, password, displayName, birthDate });
      navigate("/lista");
    } catch (err) {
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case "auth/email-already-in-use":
            setError("Este email já está em uso");
            break;
          case "auth/invalid-email":
            setError("Email inválido");
            break;
          case "auth/weak-password":
            setError("A senha deve conter pelo menos 6 caracteres");
            break;
          default:
            setError("Erro ao cadastrar. Tente novamente.");
        }
      } else {
        setError("Erro inesperado. Tente novamente.");
      }
    } finally {
      setLoading(false); // <-- Agora SEMPRE será chamado
    }
  };

  const handlePasswordReset = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResetEmailError(null);

    if (!resetEmail) {
      setResetEmailError("Informe um email");
      return;
    }

    try {
      await firebasePasswordReset(resetEmail);
      setResetEmail("");
      alert("Email de redefinição enviado!");
      setIsResettingPassword(false);
    } catch {
      setError("Erro ao enviar email de redefinição.");
    }
  };

  return (
    <div className="bg-app relative flex min-h-[100dvh] items-center justify-center overflow-x-hidden px-4 py-10 text-white">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(244,114,182,0.18),transparent_28%),radial-gradient(circle_at_80%_20%,rgba(236,72,153,0.12),transparent_18%)]" />
      <div className="panel-glass relative w-full max-w-md overflow-hidden rounded-[2rem] px-6 py-10">
        <div className="mb-8 text-center">
          <img src={logo} alt="Logo" className="mx-auto h-16" />
          <p className="mt-5 text-xs uppercase tracking-[0.28em] text-rose-200/80">
            Organize seu próximo play
          </p>
          <h1 className="mt-3 text-3xl font-semibold text-white">
            Bem-vinda ao Paralelas
          </h1>
          <p className="mt-2 text-sm text-zinc-300">
            Acesse sua conta para entrar na lista, acompanhar torneios e ver o
            histórico.
          </p>
        </div>

        {isResettingPassword ? (
          <>
            <h2 className="text-xl font-semibold mb-4">Redefinir senha</h2>
            <form onSubmit={handlePasswordReset}>
              <label className="block text-sm font-medium text-zinc-200">
                Email
              </label>
              <input
                type="email"
                className={`mt-2 w-full rounded-2xl border bg-white/6 px-4 py-3 text-white outline-none transition ${
                  resetEmailError
                    ? "border-red-500"
                    : "border-white/10 focus:border-rose-300/40"
                }`}
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
              {resetEmailError && (
                <p className="text-red-500 text-sm mt-1">{resetEmailError}</p>
              )}
              <button
                type="submit"
                className="mt-6 w-full rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 py-3 font-semibold text-white shadow-[0_18px_40px_rgba(244,114,182,0.28)] transition hover:brightness-105"
              >
                Enviar email
              </button>
              <button
                type="button"
                className="mt-3 w-full rounded-2xl border border-white/10 bg-white/10 py-3 transition hover:bg-white/14"
                onClick={() => setIsResettingPassword(false)}
              >
                Cancelar
              </button>
            </form>
          </>
        ) : (
          <>
            <form onSubmit={isRegistering ? handleRegister : handleSubmit}>
              {isRegistering && (
                <>
                  <label className="mt-4 block text-sm font-medium text-zinc-200">
                    Nome
                  </label>
                  <input
                    type="text"
                    className={`mt-2 w-full rounded-2xl border bg-white/6 px-4 py-3 text-white outline-none transition ${
                      displayNameError
                        ? "border-red-500"
                        : "border-white/10 focus:border-rose-300/40"
                    }`}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                  {displayNameError && (
                    <p className="text-red-500 text-sm mt-1">
                      {displayNameError}
                    </p>
                  )}

                  <label className="mt-4 block text-sm font-medium text-zinc-200">
                    Aniversário
                  </label>
                  <input
                    type="date"
                    className={`mt-2 w-full rounded-2xl border bg-white/6 px-4 py-3 text-white outline-none transition ${
                      birthDateError
                        ? "border-red-500"
                        : "border-white/10 focus:border-rose-300/40"
                    }`}
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                  />
                  {birthDateError && (
                    <p className="text-red-500 text-sm mt-1">
                      {birthDateError}
                    </p>
                  )}
                </>
              )}

              <label className="mt-4 block text-sm font-medium text-zinc-200">
                Email
              </label>
              <input
                type="email"
                className={`mt-2 w-full rounded-2xl border bg-white/6 px-4 py-3 text-white outline-none transition ${
                  emailError
                    ? "border-red-500"
                    : "border-white/10 focus:border-rose-300/40"
                }`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {emailError && (
                <p className="text-red-500 text-sm mt-1">{emailError}</p>
              )}

              <label className="mt-4 block text-sm font-medium text-zinc-200">
                Senha
              </label>
              <input
                type="password"
                className={`mt-2 w-full rounded-2xl border bg-white/6 px-4 py-3 text-white outline-none transition ${
                  passwordError
                    ? "border-red-500"
                    : "border-white/10 focus:border-rose-300/40"
                }`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {passwordError && (
                <p className="text-red-500 text-sm mt-1">{passwordError}</p>
              )}

              {isRegistering && (
                <>
                  <label className="mt-4 block text-sm font-medium text-zinc-200">
                    Confirmar senha
                  </label>
                  <input
                    type="password"
                    className={`mt-2 w-full rounded-2xl border bg-white/6 px-4 py-3 text-white outline-none transition ${
                      confirmPasswordError
                        ? "border-red-500"
                        : "border-white/10 focus:border-rose-300/40"
                    }`}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  {confirmPasswordError && (
                    <p className="text-red-500 text-sm mt-1">
                      {confirmPasswordError}
                    </p>
                  )}
                </>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`mt-6 flex w-full items-center justify-center gap-2 rounded-2xl py-3 font-semibold transition ${
                  loading
                    ? "cursor-not-allowed bg-rose-300/70"
                    : "bg-gradient-to-r from-pink-500 to-rose-500 shadow-[0_18px_40px_rgba(244,114,182,0.28)] hover:brightness-105"
                }`}
              >
                {loading ? (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : isRegistering ? (
                  "Cadastrar"
                ) : (
                  "Entrar"
                )}
              </button>

              {error && (
                <p className="text-red-500 text-sm mt-4 text-center">{error}</p>
              )}

              {!isRegistering && (
                <p className="text-sm text-center mt-6">
                  Esqueceu a senha?{" "}
                  <button
                    type="button"
                    onClick={() => setIsResettingPassword(true)}
                    className="text-rose-300 hover:underline"
                  >
                    Redefinir
                  </button>
                </p>
              )}

              <p className="text-sm text-center mt-4">
                {isRegistering ? "Já tem conta?" : "Não tem conta?"}{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setIsRegistering(!isRegistering);
                      setError(null);
                    }}
                    className="text-rose-300 hover:underline"
                  >
                    {isRegistering ? "Entrar" : "Cadastrar"}
                  </button>
              </p>
            </form>
          </>
        )}

        <span className="absolute bottom-3 left-1/2 -translate-x-1/2 transform text-xs text-zinc-500">
          {getVersionApp()}
        </span>
      </div>
    </div>
  );
}

export default Login;
